import { createFileRoute, notFound } from "@tanstack/react-router";
import { BlogAuthorDocument } from "@/graphql/definitions";
import { BlogAuthorPage } from "@/components/blog/BlogAuthorPage";
import { siteTitle } from "@/config/site";
import { cardMeta } from "@/lib/social-card-meta";
import { blogPostsPerPage } from "@/config/blog";
import { localizePostMetasFn, localizeAuthorFn } from "@/server/blog";
import { localeUrl, ogImageMeta } from "@/lib/seo";
import { jsonLdScripts } from "@/lib/jsonld";
import { archiveNames, englishArchiveNames, authorArchiveSeo } from "@/lib/archive-seo";
import { authorArchiveJsonLd } from "@/lib/listing-jsonld";

export const Route = createFileRoute("/$locale/blog/authors/$authorKey/")({
  loader: async ({ params, context }) => {
    const { data, error } = await context.client.query({
      query: BlogAuthorDocument,
      variables: { key: params.authorKey, page: 1, pageSize: blogPostsPerPage },
      fetchPolicy: "network-only",
    });
    if (!data) throw error ?? new Error("BlogAuthor query returned no data");
    if (!data.blogAuthor) throw notFound();
    const [posts, authorI18n] = await Promise.all([
      localizePostMetasFn({ data: { locale: params.locale, posts: [...data.blogAuthor.posts.posts] } }),
      localizeAuthorFn({ data: { locale: params.locale, author: data.blogAuthor.author } }),
    ]);
    const author = { ...data.blogAuthor.author, ...authorI18n };
    const seo = await authorArchiveSeo(params.locale, author, data.blogAuthor.posts.totalCount);
    const names = await archiveNames(params.locale, "blog", "authors");
    return {
      ...data.blogAuthor,
      author,
      posts: { ...data.blogAuthor.posts, posts },
      seo,
      names,
    };
  },
  head: ({ loaderData, params }) => {
    const canonicalUrl = localeUrl(params.locale, `/blog/authors/${params.authorKey}`);
    const names =
      loaderData?.names ?? englishArchiveNames("blog", "authors");
    const authorName = loaderData?.author.name ?? "Author";
    // `seo` is computed in the loader; guard it — head() must never throw.
    const pageTitle = loaderData?.seo?.title ?? `${authorName} | ${siteTitle}`;
    const pageDesc = loaderData?.seo?.description ?? "";
    return {
      meta: [
        { title: pageTitle },
        { name: "description", content: pageDesc },
        ...(loaderData ? ogImageMeta(loaderData.author.name) : []),
        ...cardMeta({
          title: pageTitle,
          description: pageDesc,
          canonicalUrl: canonicalUrl,
          locale: params.locale,
        }),
      ],
      scripts: loaderData
        ? jsonLdScripts(
            ...authorArchiveJsonLd({
              author: loaderData.author,
              name: loaderData.seo?.headline ?? loaderData.author.name,
              url: canonicalUrl,
              description: pageDesc,
              homeUrl: localeUrl(params.locale, "/"),
              sectionName: names.sectionName,
              listName: names.listName,
              sectionUrl: localeUrl(params.locale, "/blog"),
              posts: loaderData.posts.posts,
            }),
          )
        : undefined,
      links: [{ rel: "canonical", href: canonicalUrl }],
    };
  },
  component: function BlogAuthorRoute() {
    const detail = Route.useLoaderData();
    return <BlogAuthorPage detail={detail} />;
  },
});
